import React, { useState } from 'react';
import { AlertTriangle, GraduationCap, Loader2, RotateCcw, Trash2 } from 'lucide-react';
import { UEBUNG_HUB_TEXT } from '../../services/uebung/uebungService';

/**
 * Der gelbe Kopfstreifen eines Übungsprojekts (Plan 8.1).
 *
 * Reine Anzeige mit zwei Rückkanälen — die Arbeit (RPC, Neuladen, Rücksprung
 * in die Projektliste) macht der Aufrufer. Das ist die stehende Entscheidung
 * „ProjectHub: dünner Container, Logik in Hooks + pure Funktionen": der Hub
 * bekommt einen Baustein dazu, keine zweite Zuständigkeit.
 *
 * ZURÜCKSETZEN fragt bewusst NICHT nach. Es gibt nichts zu verlieren — der
 * Vorlagenstand ist jederzeit wiederherstellbar, und eine Rückfrage vor einem
 * folgenlosen Schritt erzieht dazu, Rückfragen wegzuklicken.
 *
 * LÖSCHEN fragt einmal. Nicht, weil etwas Wertvolles verschwände, sondern
 * weil danach die Kachel im Übungsplatz wieder „anlegen" sagt und der Nutzer
 * kurz begreifen soll, dass sein Übungsstand weg ist. Vier-Augen-Prinzip und
 * Grabstein gelten ausdrücklich nur für echte Projekte.
 */

interface Props {
    onZuruecksetzen: () => void | Promise<void>;
    onLoeschen: () => void | Promise<void>;
    /** Im Lesemodus stehen beide Knöpfe still. */
    isReadOnly?: boolean;
}

export const UebungsStreifen: React.FC<Props> = ({ onZuruecksetzen, onLoeschen, isReadOnly = false }) => {
    const [laeuft, setLaeuft] = useState<'reset' | 'delete' | null>(null);
    const [fehler, setFehler] = useState<string | null>(null);

    /**
     * `catch` ist hier nicht Kosmetik. Beide Rückkanäle sprechen mit der
     * Datenbank (RPC, Storage); ohne `catch` endete jeder Fehlschlag als
     * unbehandelte Ablehnung in der Konsole, der Spinner verschwände und der
     * Streifen stünde unverändert da — für den Nutzer nicht zu unterscheiden
     * von „nichts passiert". Die Meldung bleibt im Streifen stehen, dieselbe
     * Behandlung wie im Übungsplatz (`Uebungsplatz.tsx`).
     */
    const fuehreAus = async (was: 'reset' | 'delete', aktion: () => void | Promise<void>) => {
        if (laeuft || isReadOnly) return;
        setFehler(null);
        setLaeuft(was);
        try {
            await aktion();
        } catch (e) {
            setFehler(e instanceof Error && e.message
                ? e.message
                : was === 'reset'
                    ? 'Das Übungsprojekt konnte nicht zurückgesetzt werden.'
                    : 'Das Übungsprojekt konnte nicht gelöscht werden.');
        } finally {
            setLaeuft(null);
        }
    };

    const handleLoeschen = () => {
        if (!window.confirm('Übungsprojekt löschen? Der geübte Stand geht verloren; die Vorlage lässt sich jederzeit neu anlegen.')) return;
        void fuehreAus('delete', onLoeschen);
    };

    return (
        <div
            data-tutorial="uebung.hub-streifen"
            className="bg-amber-50 border border-amber-300 rounded-2xl px-5 py-4 flex flex-col sm:flex-row sm:items-center gap-3"
        >
            <div className="bg-white border border-amber-200 w-10 h-10 rounded-xl flex items-center justify-center shrink-0">
                <GraduationCap className="w-5 h-5 text-amber-600" />
            </div>
            <div className="flex-1 min-w-0">
                <p className="text-sm font-bold text-amber-900 leading-snug">{UEBUNG_HUB_TEXT}</p>
                {fehler ? (
                    <p className="text-[11px] text-rose-700 leading-relaxed mt-1 flex items-start gap-1.5">
                        <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-px" />
                        <span>{fehler}</span>
                    </p>
                ) : (
                    <p className="text-[11px] text-amber-700 leading-relaxed mt-0.5">
                        Alles funktioniert wie im echten Projekt — es taucht nur in keiner Auswertung,
                        keiner Fälligkeit und keinem Beleg auf.
                    </p>
                )}
            </div>
            <div className="flex items-center gap-2 shrink-0">
                <button
                    type="button"
                    disabled={isReadOnly || laeuft !== null}
                    onClick={() => { void fuehreAus('reset', onZuruecksetzen); }}
                    className="px-3.5 py-2 rounded-xl bg-white border border-amber-300 text-amber-800 text-xs font-bold flex items-center gap-1.5 hover:bg-amber-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                    {laeuft === 'reset' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RotateCcw className="w-3.5 h-3.5" />}
                    Zurücksetzen
                </button>
                <button
                    type="button"
                    disabled={isReadOnly || laeuft !== null}
                    onClick={handleLoeschen}
                    className="px-3.5 py-2 rounded-xl bg-white border border-rose-200 text-rose-700 text-xs font-bold flex items-center gap-1.5 hover:bg-rose-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                    {laeuft === 'delete' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
                    Löschen
                </button>
            </div>
        </div>
    );
};
